'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';

type Industry =
  | 'Technology'
  | 'Design'
  | 'Marketing'
  | 'Writing'
  | 'Consulting'
  | 'Finance'
  | 'Other';

const industryBenchmarks: Record<Industry, { low: number; high: number }> = {
  Technology: { low: 100, high: 200 },
  Design: { low: 75, high: 150 },
  Marketing: { low: 65, high: 140 },
  Writing: { low: 50, high: 120 },
  Consulting: { low: 100, high: 250 },
  Finance: { low: 90, high: 200 },
  Other: { low: 50, high: 150 },
};

interface Results {
  grossNeeded: number;
  annualBillableHours: number;
  minimumHourly: number;
  recommendedHourly: number;
  halfDayRate: number;
  fullDayRate: number;
  monthlyRetainer: number;
  weeklyRate: number;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export default function FreelanceRateContent() {
  const [targetIncome, setTargetIncome] = useState(100000);
  const [expenses, setExpenses] = useState(10000);
  const [taxRate, setTaxRate] = useState(30);
  const [billableHours, setBillableHours] = useState(30);
  const [vacationWeeks, setVacationWeeks] = useState(3);
  const [industry, setIndustry] = useState<Industry>('Technology');
  const [results, setResults] = useState<Results | null>(null);

  function calculate() {
    const taxDecimal = taxRate / 100;
    const grossNeeded = (targetIncome + expenses) / (1 - taxDecimal);
    const annualBillableHours = billableHours * (52 - vacationWeeks);
    const minimumHourly = grossNeeded / annualBillableHours;
    const recommendedHourly = minimumHourly * 1.2;
    const halfDayRate = recommendedHourly * 4 * 0.9;
    const fullDayRate = recommendedHourly * 8 * 0.85;
    const avgMonthlyHours = annualBillableHours / 12;
    const monthlyRetainer = recommendedHourly * avgMonthlyHours * 0.9;
    const weeklyRate = recommendedHourly * billableHours * 0.9;

    setResults({
      grossNeeded,
      annualBillableHours,
      minimumHourly,
      recommendedHourly,
      halfDayRate,
      fullDayRate,
      monthlyRetainer,
      weeklyRate,
    });
  }

  const benchmark = industryBenchmarks[industry];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100">
      <ToolHero
        title="Freelance Rate Calculator"
        description="Calculate your ideal freelance hourly and project rates. Input your target income, expenses, and billable hours to get your minimum rate."
        category="Career Tools"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Input Form */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Your Freelance Details
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Target Annual Income */}
            <div>
              <label
                htmlFor="targetIncome"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Target Annual Income
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                  $
                </span>
                <input
                  id="targetIncome"
                  type="number"
                  value={targetIncome}
                  onChange={(e) => setTargetIncome(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-xl pl-8 pr-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                />
              </div>
            </div>

            {/* Annual Business Expenses */}
            <div>
              <label
                htmlFor="expenses"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Annual Business Expenses
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                  $
                </span>
                <input
                  id="expenses"
                  type="number"
                  value={expenses}
                  onChange={(e) => setExpenses(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-xl pl-8 pr-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Software, equipment, coworking, insurance, etc.
              </p>
            </div>

            {/* Self-Employment Tax Rate */}
            <div>
              <label
                htmlFor="taxRate"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Self-Employment Tax Rate
              </label>
              <div className="relative">
                <input
                  id="taxRate"
                  type="number"
                  value={taxRate}
                  onChange={(e) => setTaxRate(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 pr-10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                  %
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Income tax + self-employment tax (typically 25-35%)
              </p>
            </div>

            {/* Industry */}
            <div>
              <label
                htmlFor="industry"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Industry
              </label>
              <select
                id="industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value as Industry)}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition bg-white"
              >
                <option value="Technology">Technology</option>
                <option value="Design">Design</option>
                <option value="Marketing">Marketing</option>
                <option value="Writing">Writing</option>
                <option value="Consulting">Consulting</option>
                <option value="Finance">Finance</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Billable Hours Per Week — full width */}
            <div className="md:col-span-2">
              <label
                htmlFor="billableHours"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Billable Hours Per Week:{' '}
                <span className="text-blue-600 font-bold">{billableHours}</span>
              </label>
              <input
                id="billableHours"
                type="range"
                min={15}
                max={45}
                value={billableHours}
                onChange={(e) => setBillableHours(Number(e.target.value))}
                className="w-full accent-blue-600 h-2 rounded-lg cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>15 hrs</span>
                <span>30 hrs</span>
                <span>45 hrs</span>
              </div>
            </div>

            {/* Weeks of Vacation */}
            <div className="md:col-span-2 max-w-xs">
              <label
                htmlFor="vacationWeeks"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Weeks of Vacation Per Year
              </label>
              <input
                id="vacationWeeks"
                type="number"
                min={0}
                max={20}
                value={vacationWeeks}
                onChange={(e) => setVacationWeeks(Number(e.target.value))}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
              />
            </div>
          </div>

          {/* Calculate Button */}
          <div className="mt-8">
            <button
              onClick={calculate}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold py-4 px-8 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-600 transition-all text-lg cursor-pointer"
            >
              Calculate My Rates
            </button>
          </div>
        </div>

        {/* Results */}
        {results && (
          <div className="mt-10 space-y-8">
            {/* Rate Cards — 2x2 Grid */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Your Freelance Rates
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Minimum Hourly */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
                  <p className="text-sm text-gray-600 mb-2">
                    Minimum Hourly Rate
                  </p>
                  <p className="text-3xl font-extrabold text-gray-900">
                    {formatCurrency(results.minimumHourly)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Your floor rate
                  </p>
                </div>

                {/* Recommended Hourly — highlighted */}
                <div className="bg-white rounded-xl border-2 border-blue-500 shadow-xl p-6 text-center relative">
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    Recommended
                  </span>
                  <p className="text-sm text-gray-600 mb-2">
                    Recommended Hourly Rate
                  </p>
                  <p className="text-3xl font-extrabold text-gray-900">
                    {formatCurrency(results.recommendedHourly)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    With 20% profit margin
                  </p>
                </div>

                {/* Half-Day Rate */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
                  <p className="text-sm text-gray-600 mb-2">
                    Half-Day Rate (4hrs)
                  </p>
                  <p className="text-3xl font-extrabold text-gray-900">
                    {formatCurrency(results.halfDayRate)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    10% volume discount
                  </p>
                </div>

                {/* Full-Day Rate */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
                  <p className="text-sm text-gray-600 mb-2">
                    Full-Day Rate (8hrs)
                  </p>
                  <p className="text-3xl font-extrabold text-gray-900">
                    {formatCurrency(results.fullDayRate)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    15% volume discount
                  </p>
                </div>
              </div>
            </div>

            {/* Additional Rate Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
                <p className="text-sm text-gray-600 mb-2">Monthly Retainer</p>
                <p className="text-3xl font-extrabold text-gray-900">
                  {formatCurrency(results.monthlyRetainer)}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {Math.round(results.annualBillableHours / 12)} hrs/mo with 10%
                  retainer discount
                </p>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
                <p className="text-sm text-gray-600 mb-2">Weekly Rate</p>
                <p className="text-3xl font-extrabold text-gray-900">
                  {formatCurrency(results.weeklyRate)}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {billableHours} hrs/wk with 10% discount
                </p>
              </div>
            </div>

            {/* Industry Benchmark */}
            <div className="bg-blue-50 rounded-xl border border-blue-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Industry Benchmark
              </h3>
              <p className="text-gray-700">
                Freelancers in{' '}
                <span className="font-semibold">{industry}</span> typically
                charge{' '}
                <span className="font-bold text-blue-700">
                  {formatCurrency(benchmark.low)}&ndash;
                  {formatCurrency(benchmark.high)}/hr
                </span>
                .{' '}
                {results.recommendedHourly < benchmark.low ? (
                  <span className="text-amber-600">
                    Your recommended rate is below the typical range. Consider
                    raising your rates or reducing expenses.
                  </span>
                ) : results.recommendedHourly > benchmark.high ? (
                  <span className="text-green-700">
                    Your recommended rate is above average — make sure your
                    portfolio and experience justify premium pricing.
                  </span>
                ) : (
                  <span className="text-green-700">
                    Your recommended rate falls within the typical range for your
                    industry.
                  </span>
                )}
              </p>
            </div>

            {/* Math Breakdown */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                How We Calculated This
              </h3>
              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span>
                    Target Income + Expenses
                  </span>
                  <span className="font-mono font-semibold">
                    {formatCurrency(targetIncome)} + {formatCurrency(expenses)} ={' '}
                    {formatCurrency(targetIncome + expenses)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span>
                    Gross Needed (accounting for {taxRate}% taxes)
                  </span>
                  <span className="font-mono font-semibold">
                    {formatCurrency(targetIncome + expenses)} / (1 - {taxRate}%) ={' '}
                    {formatCurrency(results.grossNeeded)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span>
                    Annual Billable Hours
                  </span>
                  <span className="font-mono font-semibold">
                    {billableHours} hrs/wk x (52 - {vacationWeeks}) wks ={' '}
                    {results.annualBillableHours.toLocaleString()} hrs
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span>
                    Minimum Hourly Rate
                  </span>
                  <span className="font-mono font-semibold">
                    {formatCurrency(results.grossNeeded)} /{' '}
                    {results.annualBillableHours.toLocaleString()} hrs ={' '}
                    {formatCurrency(results.minimumHourly)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span>
                    Recommended Rate (+20% profit margin)
                  </span>
                  <span className="font-mono font-bold text-blue-700">
                    {formatCurrency(results.minimumHourly)} x 1.2 ={' '}
                    {formatCurrency(results.recommendedHourly)}
                  </span>
                </div>
              </div>
            </div>

            {/* Educational Section */}
            <section className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mt-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">How to Set Your Freelance Rates With Confidence</h3>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Setting the right freelance rate is one of the most important -- and most stressful -- decisions independent professionals face. Charge too little and you burn out working unsustainable hours; charge too much without the portfolio to back it up and you lose opportunities. The key is to <strong>start with the math</strong>: calculate what you actually need to earn after taxes, expenses, and time off, then add a profit margin so your business can grow.
                </p>
                <p>
                  Most new freelancers underestimate their costs. Remember that as a self-employed professional, you are responsible for self-employment tax (15.3% in the U.S. for Social Security and Medicare alone), health insurance, retirement savings, software subscriptions, and equipment. You also cannot bill for every hour you work -- time spent on invoicing, marketing, client communication, and professional development is real work that does not appear on any invoice. Industry data suggests that freelancers typically bill only <strong>60-70% of their total working hours</strong>.
                </p>
                <p>
                  When presenting rates to clients, offer multiple pricing structures: hourly for small or unpredictable scopes, day or half-day rates for focused engagements, and monthly retainers for ongoing relationships. Retainers and project-based pricing often work in your favor because they shift the conversation from &quot;how much per hour&quot; to &quot;what is the value of the outcome.&quot; As you gain experience and testimonials, revisit your rates every 6-12 months and adjust upward to reflect your growing expertise.
                </p>
                <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
                  <strong>Sources &amp; Further Reading:</strong>{' '}
                  <a href="https://www.freelancersunion.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Freelancers Union: Resources for Independent Workers</a>
                  {' · '}
                  <a href="https://www.sba.gov/business-guide/launch-your-business/choose-business-structure" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">U.S. Small Business Administration: Business Guide</a>
                  {' · '}
                  <a href="https://www.bls.gov/ooh/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Bureau of Labor Statistics: Occupational Outlook Handbook</a>
                </p>
              </div>
            </section>

            {/* CTA */}
            <ToolCTA
              headline="Freelancing? Build a portfolio-worthy resume."
              description="A standout resume helps you land higher-paying clients. Build yours in minutes with Superpower Resume."
              buttonText="Build My Resume Free"
            />
          </div>
        )}
      </div>
    </div>
  );
}
